import React, { useState, useMemo, useEffect } from 'react';
import { FileSpreadsheet, Package, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './ui/Toast';
import { logger } from '../utils/logger';
import { getMarketplaceCurrency, CURRENCY_SYMBOLS } from '../utils/currencyExchange';
import type { MarketplaceCode, TransactionData, MarketplaceConfig } from '../types/transaction';
import { MARKETPLACE_CONFIGS } from '../constants/marketplaces';
import { calculateAnalytics } from '../services/analytics/analyticsEngine';
import { fetchProductMapping, createProductMap, enrichTransaction, detectFallbackMatches, persistFallbackMatches } from '../services/productMapping';
import { fetchTransactions, fetchTransactionStats, saveTransactions, deleteAllTransactions, deleteTransactionsByMarketplace, TransactionApiData } from '../services/api/configApi';

// Import extracted helpers and components
import { formatDateHuman } from './transaction-analyzer/helpers';
import { SummaryCards } from './transaction-analyzer/SummaryCards';
import { TransactionFilters } from './transaction-analyzer/TransactionFilters';
import { OrderDetailsCards } from './transaction-analyzer/OrderDetailsCards';
import { FulfillmentStatsCards } from './transaction-analyzer/FulfillmentStatsCards';
import { FeeDetailsSection } from './transaction-analyzer/FeeDetailsSection';
import TransactionHeader from './transaction-analyzer/TransactionHeader';
import MarketplaceSalesSection from './transaction-analyzer/MarketplaceSalesSection';
import ComparisonDetailModal from './transaction-analyzer/ComparisonDetailModal';
import DataConsistencyCheck from './DataConsistencyCheck';
import { UnprocessedTransactionsSection } from './profitability-analyzer/UnprocessedTransactionsSection';

// Component-specific types
interface TransactionAnalyzerProps {
  onDataLoaded?: (data: TransactionData[]) => void;
}

interface StoredFileMetadata {
  id: string;
  marketplace: string;
  fileName: string;
  uploadDate: Date;
  dataCount: number;
  dateRange: { start: Date; end: Date };
  sizeInBytes: number;
}

interface TransactionSavedFilters {
  marketplaceCode?: MarketplaceCode | null;
  startDate?: string;
  endDate?: string;
  dateRange?: { start: string; end: string };
  selectedFulfillment?: string;
  comparisonMode?: 'none' | 'previous-period' | 'previous-year';
}

const CONFIGS = MARKETPLACE_CONFIGS;

// Helper function to convert API data to TransactionData format
const convertApiToTransactionData = (apiData: TransactionApiData[]): TransactionData[] => {
  return apiData.map(item => ({
    id: item.id,
    fileName: item.fileName,
    date: new Date(item.date),
    dateOnly: String(item.dateOnly).split('T')[0],
    type: item.type,
    categoryType: item.categoryType,
    orderId: item.orderId,
    sku: item.sku,
    description: item.description,
    descriptionLower: item.description.toLowerCase(),
    marketplace: item.marketplace,
    fulfillment: item.fulfillment,
    orderPostal: item.orderPostal,
    quantity: typeof item.quantity === 'string' ? parseInt(item.quantity) || 0 : (item.quantity || 0),
    productSales: typeof item.productSales === 'string' ? parseFloat(item.productSales) || 0 : (item.productSales || 0),
    promotionalRebates: typeof item.promotionalRebates === 'string' ? parseFloat(item.promotionalRebates) || 0 : (item.promotionalRebates || 0),
    sellingFees: typeof item.sellingFees === 'string' ? parseFloat(item.sellingFees) || 0 : (item.sellingFees || 0),
    fbaFees: typeof item.fbaFees === 'string' ? parseFloat(item.fbaFees) || 0 : (item.fbaFees || 0),
    otherTransactionFees: typeof item.otherTransactionFees === 'string' ? parseFloat(item.otherTransactionFees) || 0 : (item.otherTransactionFees || 0),
    other: typeof item.other === 'string' ? parseFloat(item.other) || 0 : (item.other || 0),
    vat: typeof item.vat === 'string' ? parseFloat(item.vat) || 0 : (item.vat || 0),
    liquidations: typeof item.liquidations === 'string' ? parseFloat(item.liquidations) || 0 : (item.liquidations || 0),
    total: typeof item.total === 'string' ? parseFloat(item.total) || 0 : (item.total || 0),
    marketplaceCode: item.marketplaceCode,
  }));
};

// Helper function to convert TransactionData to API format
const convertTransactionDataToApi = (data: TransactionData[]): TransactionApiData[] => {
  return data.map(item => ({
    id: item.id,
    fileName: item.fileName,
    date: item.date.toISOString(),
    dateOnly: item.dateOnly || item.date.toISOString().split('T')[0],
    type: item.type,
    categoryType: item.categoryType,
    orderId: item.orderId,
    sku: item.sku,
    description: item.description,
    marketplace: item.marketplace,
    fulfillment: item.fulfillment,
    orderPostal: item.orderPostal,
    quantity: item.quantity,
    productSales: item.productSales,
    promotionalRebates: item.promotionalRebates,
    sellingFees: item.sellingFees,
    fbaFees: item.fbaFees,
    otherTransactionFees: item.otherTransactionFees,
    other: item.other,
    vat: item.vat,
    liquidations: item.liquidations,
    total: item.total,
    marketplaceCode: item.marketplaceCode,
  }));
};

const AmazonTransactionAnalyzer: React.FC<TransactionAnalyzerProps> = ({
  onDataLoaded
}) => {
  const { isAdmin, isLoading: authLoading, isAuthenticated } = useAuth();
  const toast = useToast();

  // Load saved filters from localStorage
  const loadSavedFilters = (): TransactionSavedFilters => {
    try {
      const saved = localStorage.getItem('amazonAnalyzerFilters');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  };

  const [marketplaceCode, setMarketplaceCode] = useState<MarketplaceCode | null>(() => {
    const saved = loadSavedFilters();
    return saved.marketplaceCode || null;
  });

  const config = useMemo(() => {
    return marketplaceCode ? CONFIGS[marketplaceCode] : {
      code: 'ALL',
      name: 'All',
      currency: 'Mixed',
      currencySymbol: '',
      hasVAT: true,
      vatIncludedInPrice: true,
      refundRecoveryRate: 0.20,
      grossSalesFormula: (s: number, _v: number) => s,
      hasLiquidations: false,
      hasPostalZones: false,
    } as unknown as MarketplaceConfig;
  }, [marketplaceCode]);

  const [allData, setAllData] = useState<TransactionData[]>([]);

  const dataByMarketplace = useMemo(() => {
    const map = new Map<string, TransactionData[]>();
    map.set('ALL', allData);
    allData.forEach(item => {
      const code = item.marketplaceCode || 'UNKNOWN';
      const existing = map.get(code);
      if (existing) { existing.push(item); } else { map.set(code, [item]); }
    });
    return map;
  }, [allData]);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>(() => {
    const saved = loadSavedFilters();
    return saved.dateRange || { start: '', end: '' };
  });
  const [selectedFulfillment, setSelectedFulfillment] = useState<string>(() => {
    const saved = loadSavedFilters();
    return saved.selectedFulfillment || 'all';
  });
  const [comparisonMode, setComparisonMode] = useState<'none' | 'previous-period' | 'previous-year'>(() => {
    const saved = loadSavedFilters();
    return saved.comparisonMode || 'none';
  });

  const [storedFiles, setStoredFiles] = useState<StoredFileMetadata[]>([]);
  const [saveToDBAfterLoad, setSaveToDBAfterLoad] = useState<boolean>(true);
  const [validationWarnings, setValidationWarnings] = useState<string[]>([]);

  type ComparisonDetailType = 'adjustments' | 'inventory' | 'service' | 'fbaCostBreakdown';
  const [comparisonDetailOpen, setComparisonDetailOpen] = useState<boolean>(false);
  const [comparisonDetailType, setComparisonDetailType] = useState<ComparisonDetailType | null>(null);

  const [showFileUpload, setShowFileUpload] = useState<boolean>(false);
  const [showStoredMarketplaces, setShowStoredMarketplaces] = useState<boolean>(false);
  const [showDetailedBreakdown, setShowDetailedBreakdown] = useState<boolean>(false);

  const nativeCurrency = useMemo(() => {
    if (!marketplaceCode) {
      if (allData.length > 0) {
        const firstMarketplace = allData[0].marketplace;
        const code = Object.entries(CONFIGS).find(([_, cfg]) =>
          firstMarketplace.toLowerCase().includes(cfg.code.toLowerCase())
        )?.[0] as MarketplaceCode | undefined;
        return code ? getMarketplaceCurrency(code) : null;
      }
      return null;
    }
    return getMarketplaceCurrency(marketplaceCode);
  }, [marketplaceCode, allData]);

  const formatMoney = (value: number): string => {
    if (value === null || value === undefined || isNaN(value)) return '$0';
    const targetCurrency = marketplaceCode ? nativeCurrency : 'USD';
    const currencyToUse = targetCurrency || 'USD';
    const symbol = CURRENCY_SYMBOLS[currencyToUse];
    const absValue = Math.abs(value);
    if (absValue >= 100) return `${symbol}${value.toFixed(0)}`;
    return `${symbol}${value.toFixed(2)}`;
  };

  // Shared helper to enrich transactions with product mapping
  const enrichTransactionsWithProductData = async <T extends TransactionData>(data: T[]): Promise<T[]> => {
    try {
      const productInfo = await fetchProductMapping();
      if (productInfo.length > 0) {
        const productMap = createProductMap(productInfo);
        const enriched = data.map(transaction => enrichTransaction(transaction, productMap) as T);
        const withCategory = enriched.filter(t => t.productCategory).length;
        const withCost = enriched.filter(t => t.productCost !== null && t.productCost !== undefined).length;
        logger.log(`[Enrichment] ${data.length} transactions -> ${withCategory} with category, ${withCost} with cost`);

        // Auto-persist fallback matches (fire-and-forget)
        const fallbacks = detectFallbackMatches(data, productMap);
        if (fallbacks.length > 0) {
          logger.log(`[Enrichment] Found ${fallbacks.length} fallback matches via iwasku, persisting...`);
          persistFallbackMatches(fallbacks);
        }

        return enriched;
      }
    } catch (err) {
      logger.error('[Enrichment] Failed:', err);
    }
    return data;
  };

  const processFile = async (file: File, detectedMarketplace?: MarketplaceCode): Promise<TransactionData[]> => {
    // Dynamic import keeps the heavy xlsx dependency out of the main bundle —
    // it loads only when the user actually uploads a file.
    const { processExcelFile } = await import('../services/fileProcessor');
    return processExcelFile(file, detectedMarketplace);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFiles = Array.from(e.target.files || []);
    if (uploadedFiles.length === 0) return;

    setLoading(true);
    setError(null);
    setValidationWarnings([]);

    try {
      const warnings: string[] = [];
      const detectedMarketplaces = new Set<MarketplaceCode>();
      let totalAdded = 0;
      let totalSkipped = 0;
      let totalFailed = 0;

      for (const file of uploadedFiles) {
        const { detectMarketplaceFromFile } = await import('../services/fileProcessor');
        const quickRead = await detectMarketplaceFromFile(file);
        if (!quickRead) throw new Error(`${file.name}: Marketplace tespit edilemedi.`);
        detectedMarketplaces.add(quickRead);

        let data = await processFile(file, quickRead);
        data = await enrichTransactionsWithProductData(data);

        if (data.length > 500000) {
          throw new Error(`File too large: ${file.name} (${data.length.toLocaleString()} transactions). Max 500,000.`);
        }

        if (saveToDBAfterLoad && data.length > 0) {
          try {
            const apiData = convertTransactionDataToApi(data);
            const result = await saveTransactions(apiData);
            totalAdded += result.inserted;
            totalSkipped += result.updated;
            totalFailed += result.errors;
            // Backend batch errors come back inside a 200 — without this a partly written month looked «loaded» (audit 2026-09-24)
            if (result.errors > 0) warnings.push(`${file.name}: ${result.errors} of ${result.total} transactions could NOT be saved — this period is incomplete, upload the file again`);
            if (result.updated > 0) warnings.push(`${file.name}: ${result.updated} existing transactions updated`);
            logger.log(`[Upload] ${file.name}: ${result.inserted} new, ${result.updated} updated`);
          } catch (err: unknown) {
            warnings.push(`${file.name} could not be saved to API: ${err instanceof Error ? err.message : String(err)}`);
          }
        }
      }

      if (detectedMarketplaces.size > 0) {
        const marketplaceNames = Array.from(detectedMarketplaces).map(code => CONFIGS[code].name).join(', ');
        if (detectedMarketplaces.size === 1) {
          setMarketplaceCode(Array.from(detectedMarketplaces)[0]);
        } else if (!marketplaceCode) {
          setMarketplaceCode(Array.from(detectedMarketplaces)[0]);
          warnings.push(`Multiple marketplaces detected (${marketplaceNames}). You can select from filters.`);
        }
      }

      if (totalAdded > 0 || totalSkipped > 0 || totalFailed > 0) {
        warnings.unshift(`Total: ${totalAdded} new transactions added, ${totalSkipped} updated${totalFailed > 0 ? `, ${totalFailed} FAILED` : ''}`);
      }
      if (warnings.length > 0) setValidationWarnings(warnings);

      await loadDataFromDatabase(true);
      const stats = await fetchTransactionStats();
      if (stats.total > 0) {
        const files = stats.byMarketplace.map(mp => ({
          id: mp.marketplace_code,
          marketplace: mp.marketplace_code,
          fileName: `${mp.marketplace_code} - ${mp.count} transactions`,
          uploadDate: new Date(mp.max_date),
          dataCount: mp.count,
          dateRange: { start: new Date(mp.min_date), end: new Date(mp.max_date) },
          sizeInBytes: 0
        }));
        setStoredFiles(files);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const loadDataFromDatabase = async (notifyParent: boolean = false) => {
    try {
      setLoading(true);
      const params: { marketplace?: string; limit: number } = { limit: 500000 };
      if (marketplaceCode) params.marketplace = marketplaceCode;
      const response = await fetchTransactions(params);
      let transactions = convertApiToTransactionData(response.data);
      transactions = await enrichTransactionsWithProductData(transactions);
      setAllData(transactions.filter(t => t.fileName !== 'sp-api-sync'));
      if (notifyParent && onDataLoaded) onDataLoaded(transactions);
    } catch (err: unknown) {
      setError(`Error loading data: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const loadDataFromIndexedDB = loadDataFromDatabase;

  // Load all stored data on mount — wait for auth to be ready
  useEffect(() => {
    if (authLoading || !isAuthenticated) return;
    const loadAllStoredData = async () => {
      try {
        setLoading(true);
        const stats = await fetchTransactionStats();
        if (stats.total > 0) {
          const files = stats.byMarketplace.map(mp => ({
            id: mp.marketplace_code,
            marketplace: mp.marketplace_code,
            fileName: `${mp.marketplace_code} - ${mp.count} transactions`,
            uploadDate: new Date(mp.max_date),
            dataCount: mp.count,
            dateRange: { start: new Date(mp.min_date), end: new Date(mp.max_date) },
            sizeInBytes: 0
          }));
          setStoredFiles(files);

          const response = await fetchTransactions({ limit: 500000 });
          let allTransactions = convertApiToTransactionData(response.data);
          if (allTransactions.length > 0) {
            allTransactions = await enrichTransactionsWithProductData(allTransactions);
            const displayData = allTransactions.filter(t => t.fileName !== 'sp-api-sync');
            if (allTransactions.length > 500000) {
              toast.warning(`Very large dataset (${allTransactions.length.toLocaleString()} transactions).`, 6000);
              setAllData(displayData);
              if (stats.byMarketplace.length === 1) setMarketplaceCode(stats.byMarketplace[0].marketplace_code as MarketplaceCode);
            } else {
              setAllData(displayData);
              if (stats.byMarketplace.length === 1) setMarketplaceCode(stats.byMarketplace[0].marketplace_code as MarketplaceCode);
              if (onDataLoaded) onDataLoaded(allTransactions);
            }
          }
        }
      } catch (err: unknown) {
        logger.error('[TransactionAnalyzer] Failed to load data on mount:', err);
      } finally {
        setLoading(false);
      }
    };
    loadAllStoredData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated]);

  // Reload when marketplace changes
  useEffect(() => {
    if (allData.length > 0) loadDataFromIndexedDB(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketplaceCode]);

  // Save filters to localStorage
  useEffect(() => {
    localStorage.setItem('amazonAnalyzerFilters', JSON.stringify({
      marketplaceCode, dateRange, selectedFulfillment, comparisonMode,
    }));
  }, [marketplaceCode, dateRange, selectedFulfillment, comparisonMode]);

  const removeMarketplace = async (marketplaceCode: string) => {
    if (!window.confirm(`${marketplaceCode} için tüm transactionlar silinecek. Emin misiniz?`)) return;
    try {
      const deleted = await deleteTransactionsByMarketplace(marketplaceCode);
      toast.success(`${deleted} transaction silindi.`);
      window.location.reload();
    } catch (err) {
      toast.error(`Silme başarısız: ${err instanceof Error ? err.message : 'bilinmeyen hata'}`);
    }
  };

  const exportToPDF = () => { if (analytics && config) window.print(); };
  const exportToExcel = async () => {
    if (!analytics || !config) return;
    // Dynamic import: xlsx loads only when the user exports, not on page load.
    const { exportTransactionsToExcel } = await import('../utils/excelExport');
    exportTransactionsToExcel({ analytics, config, dateRange, selectedFulfillment, filteredData });
  };

  // Filtered data (marketplace + date only, not fulfillment)
  const filteredData = useMemo(() => {
    const marketplaceData = marketplaceCode
      ? dataByMarketplace.get(marketplaceCode) || []
      : dataByMarketplace.get('ALL') || [];

    if (!dateRange.start && !dateRange.end) return marketplaceData;

    return marketplaceData.filter(item => {
      const itemDateOnly = item.dateOnly || (item.date ? item.date.toISOString().split('T')[0] : '');
      if (!itemDateOnly) return false;
      const afterOrEqualStart = !dateRange.start || itemDateOnly >= dateRange.start;
      const beforeOrEqualEnd = !dateRange.end || itemDateOnly <= dateRange.end;
      return afterOrEqualStart && beforeOrEqualEnd;
    });
  }, [dataByMarketplace, dateRange.start, dateRange.end, marketplaceCode]);

  const derivedRange = useMemo(() => {
    if (dateRange.start && dateRange.end) {
      return { startStr: formatDateHuman(new Date(dateRange.start)), endStr: formatDateHuman(new Date(dateRange.end)) };
    }
    if (allData && allData.length > 0) {
      let minTime = Infinity, maxTime = -Infinity;
      for (const item of allData) {
        if (item.date) {
          const time = new Date(item.date).getTime();
          if (time < minTime) minTime = time;
          if (time > maxTime) maxTime = time;
        }
      }
      if (minTime !== Infinity && maxTime !== -Infinity) {
        return { startStr: formatDateHuman(new Date(minTime)), endStr: formatDateHuman(new Date(maxTime)) };
      }
    }
    return { startStr: "", endStr: "" };
  }, [dateRange.start, dateRange.end, allData]);

  const comparisonRanges = useMemo(() => {
    if (!dateRange.start || !dateRange.end || comparisonMode === 'none') return null;
    const currentStart = new Date(dateRange.start);
    const currentEnd = new Date(dateRange.end);
    const periodDays = Math.ceil((currentEnd.getTime() - currentStart.getTime()) / (1000 * 60 * 60 * 24));

    if (comparisonMode === 'previous-period') {
      const prevEnd = new Date(currentStart); prevEnd.setDate(prevEnd.getDate() - 1);
      const prevStart = new Date(prevEnd); prevStart.setDate(prevStart.getDate() - periodDays + 1);
      return { previousPeriod: { start: prevStart, end: prevEnd, label: `${formatDateHuman(prevStart)} - ${formatDateHuman(prevEnd)}` } };
    } else if (comparisonMode === 'previous-year') {
      const prevYearStart = new Date(currentStart); prevYearStart.setFullYear(prevYearStart.getFullYear() - 1);
      const prevYearEnd = new Date(currentEnd); prevYearEnd.setFullYear(prevYearEnd.getFullYear() - 1);
      return { previousYear: { start: prevYearStart, end: prevYearEnd, label: `${formatDateHuman(prevYearStart)} - ${formatDateHuman(prevYearEnd)}` } };
    }
    return null;
  }, [dateRange.start, dateRange.end, comparisonMode]);

  const analytics = useMemo(() => {
    return calculateAnalytics({
      data: filteredData,
      marketplaceCode,
      selectedFulfillment: selectedFulfillment as 'all' | 'FBA' | 'FBM',
      dateRange: { start: dateRange.start ? new Date(dateRange.start) : null, end: dateRange.end ? new Date(dateRange.end) : null },
      config: config as MarketplaceConfig
    });
  }, [filteredData, marketplaceCode, selectedFulfillment, dateRange.start, dateRange.end, config]);

  const comparisonAnalytics = useMemo(() => {
    if (!comparisonRanges || !config || comparisonMode === 'none') return null;
    const range = comparisonMode === 'previous-period' ? comparisonRanges.previousPeriod : comparisonRanges.previousYear;
    if (!range) return null;
    const result = calculateAnalytics({
      data: allData, marketplaceCode,
      selectedFulfillment: selectedFulfillment as 'all' | 'FBA' | 'FBM',
      dateRange: { start: range.start, end: range.end },
      config: config as MarketplaceConfig
    });
    if (result) return { ...result, label: range.label };
    return result;
  }, [comparisonMode, comparisonRanges, config, allData, marketplaceCode, selectedFulfillment]);

  const handleClearAll = async () => {
    if (window.confirm('All data will be deleted! Are you sure?')) {
      try {
        await deleteAllTransactions();
        localStorage.removeItem('productMapping_cache');
        localStorage.removeItem('productMapping_timestamp');
        localStorage.removeItem('profitability_filters');
        localStorage.removeItem('amazonAnalyzerFilters');
        setAllData([]);
        setStoredFiles([]);
        toast.success('All data cleared! Reloading...');
        setTimeout(() => { window.location.reload(); }, 500);
      } catch (err: unknown) {
        logger.error('Clear error:', err);
        toast.error(`Failed to clear data: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <style>{`
        @media print {
          body { background: white !important; print-color-adjust: exact; -webkit-print-color-adjust: exact; margin: 0; padding: 0; }
          .no-print { display: none !important; }
          .page-break { page-break-before: always; }
          @page { size: A4; margin: 1.5cm; }
          .print-block { page-break-inside: avoid; break-inside: avoid; }
          .print-single-col { grid-template-columns: 1fr !important; }
          .print-card { padding: 0.75rem !important; margin-bottom: 0.5rem !important; }
          .print-card h3 { font-size: 0.875rem !important; margin-bottom: 0.5rem !important; }
          .print-card .text-2xl { font-size: 1.25rem !important; }
          .print-card .text-lg { font-size: 0.875rem !important; }
          svg { max-width: 100%; height: auto; max-height: 200px; }
          .print-header { margin-bottom: 1rem !important; padding-bottom: 0.5rem !important; border-bottom: 2px solid #e2e8f0; }
          .space-y-3 > * { margin-top: 0.25rem !important; margin-bottom: 0.25rem !important; padding-top: 0.25rem !important; padding-bottom: 0.25rem !important; }
        }
      `}</style>
      <div className="max-w-7xl mx-auto">
        {/* Header with File Upload and Actions */}
        <TransactionHeader
          isAdmin={isAdmin}
          allDataLength={allData.length}
          loading={loading}
          error={error}
          validationWarnings={validationWarnings}
          showFileUpload={showFileUpload}
          setShowFileUpload={setShowFileUpload}
          saveToDBAfterLoad={saveToDBAfterLoad}
          setSaveToDBAfterLoad={setSaveToDBAfterLoad}
          handleFileUpload={handleFileUpload}
          storedFiles={storedFiles}
          showStoredMarketplaces={showStoredMarketplaces}
          setShowStoredMarketplaces={setShowStoredMarketplaces}
          removeMarketplace={removeMarketplace}
          exportToPDF={exportToPDF}
          exportToExcel={exportToExcel}
          hasAnalytics={!!analytics}
          onClearAll={handleClearAll}
        />

        {/* Transaction Filters */}
        {allData.length > 0 && (
          <TransactionFilters
            marketplaceCode={marketplaceCode}
            dateRange={dateRange}
            selectedFulfillment={selectedFulfillment}
            comparisonMode={comparisonMode}
            storedFiles={storedFiles}
            comparisonDateRange={
              comparisonMode === 'previous-period' ? comparisonRanges?.previousPeriod
              : comparisonMode === 'previous-year' ? comparisonRanges?.previousYear
              : null
            }
            onMarketplaceChange={setMarketplaceCode}
            onDateRangeChange={setDateRange}
            onFulfillmentChange={setSelectedFulfillment}
            onComparisonModeChange={setComparisonMode}
          />
        )}

        {loading && allData.length > 0 && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-lg font-medium text-slate-700">Analyzing...</p>
            </div>
          </div>
        )}

        {!loading && analytics && (
          <>
            {/* Print Header */}
            <div className="hidden print:block mb-6 print-header">
              <h1 className="text-2xl font-bold text-slate-800">Amazon Transaction Analyzer Report</h1>
              <p className="text-slate-600 mt-1">
                {[config?.name, derivedRange.startStr && derivedRange.endStr ? `${derivedRange.startStr} - ${derivedRange.endStr}` : "All Time", selectedFulfillment !== "all" ? selectedFulfillment : null].filter(Boolean).join("  ")}
              </p>
              <p className="text-slate-500 text-sm">Generated: {new Date().toLocaleString()}</p>
            </div>

            {/* Unknown Category Types Check */}
            <div className="no-print">
              <UnprocessedTransactionsSection transactions={filteredData} marketplaceCode={marketplaceCode} formatMoney={formatMoney} />
            </div>

            {/* Data Consistency Check */}
            <div className="mb-4 no-print">
              <DataConsistencyCheck
                transactions={allData}
                analyzerMetrics={{
                  transaction: {
                    totalRevenue: analytics?.totalSales || 0,
                    totalOrders: analytics?.totalOrders || 0,
                    advertisingCost: analytics?.displayAdvertisingCost || 0,
                    refundCount: analytics?.totalRefunds || 0,
                    marketplace: marketplaceCode || 'all',
                    dateRange: { start: dateRange.start || null, end: dateRange.end || null }
                  }
                }}
                marketplace={marketplaceCode || 'all'}
                startDate={dateRange.start || null}
                endDate={dateRange.end || null}
                selectedFulfillment={selectedFulfillment as 'all' | 'FBA' | 'FBM'}
              />
            </div>

            {/* Summary Cards */}
            <SummaryCards
              analytics={analytics}
              comparisonAnalytics={comparisonAnalytics}
              selectedFulfillment={selectedFulfillment}
              formatMoney={formatMoney}
            />

            {/* Marketplace Sales, Pie Charts, FBA Cost, Postal Zones */}
            <MarketplaceSalesSection
              analytics={analytics}
              comparisonAnalytics={comparisonAnalytics}
              marketplaceCode={marketplaceCode}
              config={config}
              selectedFulfillment={selectedFulfillment}
              formatMoney={formatMoney}
              onOpenComparisonDetail={(type) => {
                setComparisonDetailType(type);
                setComparisonDetailOpen(true);
              }}
            />

            {/* Order Details Cards */}
            <OrderDetailsCards analytics={analytics} selectedFulfillment={selectedFulfillment} formatMoney={formatMoney} />

            {/* Fulfillment Stats Cards */}
            <FulfillmentStatsCards analytics={analytics} selectedFulfillment={selectedFulfillment} formatMoney={formatMoney} />

            {/* Detailed Breakdown Section - Collapsible */}
            <div className="mb-6 no-print">
              <button
                onClick={() => setShowDetailedBreakdown(!showDetailedBreakdown)}
                className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-slate-100 hover:from-slate-100 hover:to-slate-200 rounded-lg border border-slate-200 transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-600 rounded-lg">
                    <Package className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-slate-800">Detailed Fee Breakdown</h3>
                    <p className="text-xs text-slate-600">Fee details, adjustments, and more</p>
                  </div>
                </div>
                {showDetailedBreakdown ? <ChevronUp className="w-5 h-5 text-slate-600" /> : <ChevronDown className="w-5 h-5 text-slate-600" />}
              </button>
            </div>

            {/* Fee Details Section */}
            {showDetailedBreakdown && (
              <FeeDetailsSection
                analytics={analytics}
                comparisonAnalytics={comparisonAnalytics}
                selectedFulfillment={selectedFulfillment}
                marketplaceCode={marketplaceCode}
                formatMoney={formatMoney}
                onOpenComparisonDetail={(type) => {
                  setComparisonDetailType(type);
                  setComparisonDetailOpen(true);
                }}
              />
            )}
          </>
        )}

        {allData.length === 0 && !loading && (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <FileSpreadsheet className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-800 mb-2">No files uploaded yet</h3>
            <p className="text-slate-600">Upload your transaction reports from above to get started</p>
          </div>
        )}
      </div>

      {/* Comparison Detail Modal */}
      <ComparisonDetailModal
        isOpen={comparisonDetailOpen}
        detailType={comparisonDetailType}
        comparisonAnalytics={comparisonAnalytics}
        analytics={analytics}
        comparisonMode={comparisonMode}
        comparisonRanges={comparisonRanges}
        allData={allData}
        marketplaceCode={marketplaceCode}
        dateRange={dateRange}
        formatMoney={formatMoney}
        onClose={() => setComparisonDetailOpen(false)}
      />
    </div>
  );
};

export default AmazonTransactionAnalyzer as typeof AmazonTransactionAnalyzer & { displayName: 'TransactionAnalyzer' };

// Alternative named export
export { AmazonTransactionAnalyzer as TransactionAnalyzer };
